import { IClientResponse } from '@orbis-systems/omni-sdk-ts/dist/types/apis/core/client';
import { find } from 'lodash';
import { inject, injectable } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import { ICache } from '../Cache';
import { SecurityType, TimeRange, TopPositionsType } from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import { Account, MiddlewareConfig, Quote, Sdks } from '../types';
import { AccountWithExtraInfo, PositionAccount } from '../types/Account.type';
import {
    AccountPositionBreakdownStat,
    AccountSecurityTypeBreakdownStat,
    AccountSizesBreakdownStat,
    AccountTopPositionsBreakdownStat,
    AccountTypesBreakdownStat,
    MarketCapBreakdownStat,
    QuoteHistory,
    QuoteStat,
    SectorsBreakdownStat,
    TopPositionsStat,
} from '../types/Stat.type';
import { formatMarketCapBreakdownChartData, getEtfToCompare } from '../utils';
import {
    formatAccountPositionTypeBreakdownStat,
    formatAccountSecurityTypeBreakdownStat,
    formatAccountSizesBreakdownStat,
    formatAccountTypesBreakdownStat,
    formatQuoteStat,
    formatSectorsBreakdownStat,
    formatTopPositionsBreakdownChartData,
    formatTopPositionsStat,
} from '../utils/statUtils';
import { IAccountApi, IBranchApi, IGroupApi, IModelApi } from './interfaces';
import { IQuoteApi } from './interfaces/IQuoteApi';
import { IStatApi } from './interfaces/IStatApi';

@injectable()
export class StatApi extends CachedResponseApi implements IStatApi {
    constructor(
        @inject(MiddlewareInjectToken.Sdks) sdks: Sdks,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.BranchApi) private branchApi: IBranchApi,
        @inject(ApiInjectToken.ModelApi) private modelApi: IModelApi,
        @inject(ApiInjectToken.AccountApi) private accountApi: IAccountApi,
        @inject(ApiInjectToken.GroupApi) private groupApi: IGroupApi,
        @inject(ApiInjectToken.QuoteApi) private quoteApi: IQuoteApi,
    ) {
        super(sdks, config, cache);
    }

    async getBranchTopPositionsStat(by = TopPositionsType.Weight, maxPositions = 10): Promise<TopPositionsStat> {
        const portfolio = await this.branchApi.getPortfolio();
        return formatTopPositionsStat(portfolio.entries, by, maxPositions);
    }

    async getBranchMarketCapBreakdownStat(): Promise<MarketCapBreakdownStat> {
        const portfolio = await this.branchApi.getPortfolio();
        return formatMarketCapBreakdownChartData(portfolio.entries);
    }

    async getBranchSectorsBreakdownStat(): Promise<SectorsBreakdownStat> {
        const [sectors, groups] = await Promise.all([
            this.sdks.orbis.get('v2/advisory/analytics/overexposure', undefined, {
                urlQuery: ['Sector', 'branch', 'all'],
                params: { threshold: 0 },
            }),
            this.sdks.orbis.get('v2/advisory/analytics/overexposure', undefined, {
                urlQuery: ['IndustryGroup', 'branch', 'all'],
                params: { threshold: 0 },
            }),
        ]);

        return formatSectorsBreakdownStat(sectors.data, groups.data);
    }

    async getBranchAccountSizesBreakdownStat(): Promise<AccountSizesBreakdownStat> {
        const accounts = await this.branchApi.getAccounts();
        return formatAccountSizesBreakdownStat(accounts);
    }

    async getQuoteAccountSizesBreakdownStat(symbol: string): Promise<AccountSizesBreakdownStat> {
        const data = await Promise.all([
            this.branchApi.getAccounts(),
            this.sdks.orbis.get('user/position/search', undefined, {
                params: { symbols: symbol, loadQuotes: true },
            }),
        ]);
        const allAccounts = data[0];
        const accountsHoldingPosition = data[1].data as PositionAccount[];
        const accountIds = accountsHoldingPosition.map((position) => position.accountId);
        const filteredAccounts = allAccounts.filter((account) => accountIds.includes(account.accountId));
        return formatAccountSizesBreakdownStat(filteredAccounts);
    }

    async getQuoteAccountTypesBreakdownStat(symbol: string): Promise<AccountTypesBreakdownStat> {
        const data = await Promise.all([
            this.branchApi.getAccounts(),
            this.sdks.orbis.get('user/position/search', undefined, {
                params: { symbols: symbol, loadQuotes: true },
            }),
        ]);
        const allAccounts = data[0];
        const accountsHoldingPosition = data[1].data as PositionAccount[];
        const accountsHoldingPositionIds = accountsHoldingPosition.map((position) => position.accountId);
        return formatAccountTypesBreakdownStat(
            allAccounts.filter((account) => accountsHoldingPositionIds.includes(account.accountId)),
        );
    }

    async getBranchAccountTypesBreakdownStat(): Promise<AccountTypesBreakdownStat> {
        const { data } = await this.sdks.orbis.get('v2/advisory/users/accounts/stats');
        return formatAccountTypesBreakdownStat(data);
    }

    async getModelTopPositionsStat(
        modelId: number,
        by = TopPositionsType.Weight,
        maxPositions = 10,
    ): Promise<TopPositionsStat> {
        const portfolio = await this.modelApi.getPortfolio(modelId);
        return formatTopPositionsStat(portfolio.entries, by, maxPositions);
    }

    async getModelMarketCapBreakdownStat(modelId: number): Promise<MarketCapBreakdownStat> {
        const portfolio = await this.modelApi.getPortfolio(modelId);
        return formatMarketCapBreakdownChartData(portfolio.entries);
    }

    async getModelSectorsBreakdownStat(modelId: number): Promise<SectorsBreakdownStat> {
        const [sectors, groups] = await Promise.all([
            this.sdks.orbis.get('v2/advisory/analytics/overexposure', undefined, {
                urlQuery: ['Sector', 'model', modelId],
                params: { threshold: 0 },
            }),
            this.sdks.orbis.get('v2/advisory/analytics/overexposure', undefined, {
                urlQuery: ['IndustryGroup', 'model', modelId],
                params: { threshold: 0 },
            }),
        ]);

        return formatSectorsBreakdownStat(sectors.data, groups.data);
    }

    async getModelAccountSizesBreakdownStat(modelId: number): Promise<AccountSizesBreakdownStat> {
        const data = await Promise.all([
            this.modelApi.getModels(),
            this.sdks.orbis.get('v2/advisory/model/rtbs', undefined, { urlQuery: modelId }),
        ]);

        const accounts: Account[] = data[0].find((model) => model.id === modelId)?.accounts || [];
        const accountsWithExtraInfo: AccountWithExtraInfo[] = accounts.map((account) => {
            const {
                balance = 0,
                cashOnAccount = 0,
                markToMarket = 0,
                sodCashBalance = 0,
                sodMarginBalance = 0,
                sodShortBalance = 0,
                totalDividendAmount = 0,
                totalDividendYield = 0,
            } = find(data[1].data, (item) => item.accountId === account.accountId) || {};

            return {
                ...account,
                balance,
                cashOnAccount,
                markToMarket,
                sodCashBalance,
                sodMarginBalance,
                sodShortBalance,
                totalDividendAmount,
                totalDividendYield,
            };
        });

        return formatAccountSizesBreakdownStat(accountsWithExtraInfo);
    }

    async getModelAccountTypesBreakdownStat(modelId: number): Promise<AccountTypesBreakdownStat> {
        const { data } = await this.sdks.orbis.get('v2/advisory/model/accounts/stats', undefined, {
            urlQuery: modelId,
        });
        return formatAccountTypesBreakdownStat(data);
    }

    async getAccountTopPositionsStat(
        accountId: number,
        by = TopPositionsType.Weight,
        maxPositions = 10,
    ): Promise<TopPositionsStat> {
        const portfolio = await this.accountApi.getPortfolio(accountId);
        return formatTopPositionsStat(portfolio.entries, by, maxPositions);
    }

    async getAccountTopPositionsBreakdownStat(
        accountId: number,
        topPositions?: number,
    ): Promise<AccountTopPositionsBreakdownStat> {
        const portfolio = await this.accountApi.getPortfolio(accountId);
        return formatTopPositionsBreakdownChartData(portfolio.entries, topPositions);
    }

    async getAccountMarketCapBreakdownStat(accountId: number): Promise<MarketCapBreakdownStat> {
        const portfolio = await this.accountApi.getPortfolio(accountId);
        return formatMarketCapBreakdownChartData(portfolio.entries);
    }

    async getAccountSectorsBreakdownStat(accountId: number): Promise<SectorsBreakdownStat> {
        const [sectors, groups] = await Promise.all([
            this.sdks.orbis.get('v2/advisory/analytics/overexposure', undefined, {
                urlQuery: ['Sector', 'account', accountId],
                params: { threshold: 0 },
            }),
            this.sdks.orbis.get('v2/advisory/analytics/overexposure', undefined, {
                urlQuery: ['IndustryGroup', 'account', accountId],
                params: { threshold: 0 },
            }),
        ]);

        const etfSymbols: string[] = (
            await Promise.all(
                (groups.data as { quote: Quote }[]).map((group) =>
                    this.sdks.orbis.get('research', undefined, {
                        urlQuery: group.quote.option ? group.quote.underlying?.symbol : group.quote.symbol,
                    }),
                ),
            )
        )
            .filter((item: IClientResponse) => item.data.etf)
            .map((item) => item.data.etfSymbolEntry.symbol);

        const etfSectors = await Promise.all(
            etfSymbols
                .map((symbol) => this.sdks.orbis.get('research/etf/details', undefined, { params: { symbol: symbol } }))
                .map((item) => item.then((data) => data.data.Sectors ?? [])),
        );

        return formatSectorsBreakdownStat(sectors.data, groups.data, etfSectors);
    }

    async getGroupTopPositionsStat(
        groupId: number,
        by = TopPositionsType.Weight,
        maxPositions = 10,
    ): Promise<TopPositionsStat> {
        const portfolio = await this.groupApi.getPortfolio(groupId);
        return formatTopPositionsStat(portfolio.entries, by, maxPositions);
    }

    async getGroupMarketCapBreakdownStat(groupId: number): Promise<MarketCapBreakdownStat> {
        const portfolio = await this.groupApi.getPortfolio(groupId);
        return formatMarketCapBreakdownChartData(portfolio.entries);
    }

    async getGroupAccountSizesBreakdownStat(groupId: number): Promise<AccountSizesBreakdownStat> {
        const group = await this.groupApi.getGroup(groupId);
        return formatAccountSizesBreakdownStat(group?.accounts || []);
    }

    async getGroupSectorsBreakdownStat(groupId: number): Promise<SectorsBreakdownStat> {
        const [sectors, groups] = await Promise.all([
            this.sdks.orbis.get('v2/advisory/analytics/overexposure', undefined, {
                urlQuery: ['Sector', 'group', groupId],
                params: { threshold: 0 },
            }),
            this.sdks.orbis.get('v2/advisory/analytics/overexposure', undefined, {
                urlQuery: ['IndustryGroup', 'group', groupId],
                params: { threshold: 0 },
            }),
        ]);

        return formatSectorsBreakdownStat(sectors.data, groups.data);
    }

    async getGroupAccountTypesBreakdownStat(groupId: number): Promise<AccountTypesBreakdownStat> {
        const { data } = await this.sdks.orbis.get('v2/advisory/group/accounts/stats', undefined, {
            urlQuery: groupId,
        });
        return formatAccountTypesBreakdownStat(data);
    }

    async getAccountPositionTypeBreakdownStat(accountId: number): Promise<AccountPositionBreakdownStat> {
        const portfolio = await this.accountApi.getPortfolio(accountId);
        return formatAccountPositionTypeBreakdownStat(portfolio.entries);
    }

    async getAccountSecurityTypeBreakdownStat(accountId: number): Promise<AccountSecurityTypeBreakdownStat> {
        const portfolio = await this.accountApi.getPortfolio(accountId);
        const positionsWithSecurityType = await Promise.all(
            portfolio.entries.map(async (position) => {
                const research = await this.sdks.orbis.get('research', undefined, {
                    urlQuery: position.quote.underlyingSymbol ?? position.symbol,
                });
                return {
                    ...position,
                    securityType: position.option
                        ? SecurityType.Option
                        : research.data.etf
                        ? SecurityType.Etf
                        : SecurityType.Equity,
                };
            }),
        );
        return formatAccountSecurityTypeBreakdownStat(positionsWithSecurityType);
    }

    async getUserAccountTypesBreakdownStat(userId: number): Promise<AccountTypesBreakdownStat> {
        const { data } = await this.sdks.orbis.get('v2/advisory/users/accounts', undefined, {
            params: { uid: userId, loadRtb: true },
        });
        const accounts: Account[] = data.map((item: { account: Account }) => item.account);
        return formatAccountTypesBreakdownStat(accounts);
    }

    async getQuoteStat(symbol: string, range: TimeRange, crypto?: boolean): Promise<QuoteStat> {
        const [data, etfData] = await Promise.all(
            crypto && range === TimeRange.OneDay
                ? // @ts-ignore: URL not in SDK
                  // FIX ME: Change to /crypto when back end fixes
                  [this.sdks.orbis.get('quotes/equity/intraday', undefined, { params: { symbol } })]
                : crypto
                ? [
                      // @ts-ignore: URL not in SDK
                      this.sdks.orbis.get('quotes/crypto/historical', undefined, {
                          params: { symbol, range },
                      }),
                  ]
                : range === TimeRange.OneDay
                ? [
                      this.sdks.orbis.get('quotes/equity/intraday', undefined, { params: { symbol } }),
                      this.sdks.orbis.get('quotes/equity/intraday', undefined, {
                          params: { symbol: getEtfToCompare(symbol) },
                      }),
                  ]
                : [
                      this.sdks.orbis.get('quotes/equity/historical', undefined, {
                          params: { symbol, range },
                      }),
                      this.sdks.orbis.get('quotes/equity/historical', undefined, {
                          params: { symbol: getEtfToCompare(symbol), range },
                      }),
                  ],
        );

        // @ts-ignore: TODO: URL not added / Will be removed when back end adds
        const currentQuote = await this.quoteApi.getQuote(symbol, crypto);
        const combinedData = currentQuote ? [...data.data, currentQuote] : [data.data];
        return formatQuoteStat(combinedData, crypto ? [] : etfData?.data);
    }

    async getQuoteIntraday(symbol: string): Promise<QuoteHistory[]> {
        const response = await this.sdks.orbis.get('quotes/equity/intraday', undefined, { params: { symbol } });
        return response.data;
    }

    async getQuoteHistorical(symbol: string, range?: TimeRange): Promise<QuoteHistory[]> {
        const response = await this.sdks.orbis.get('quotes/equity/historical', undefined, {
            params: { symbol, range: range ?? TimeRange.SevenMonth },
        });
        return response.data;
    }
}
